<template>

    <div class="px-6 py-4 bg-minplan-beige -mb-4">
        <page-title :title="$t('agencies.addTeamMember')"></page-title>
    </div>

    <jet-form-section @submitted="addTeamMember">

        <template #form>
            <!-- Card Team Member Information -->
            <form-card :title="$t('agencies.member')" :description="$t('agencies.memberDescription')">

                <div class="col-span-6">
                    <jet-label for="firstname">{{ $t('agencies.firstname') }} <RequiredMark /></jet-label>
                    <jet-input id="firstname" type="text" class="mt-1 block w-full" v-model="form.firstname" />
                    <jet-input-error :message="form.errors.firstname" class="mt-2" />
                </div>

                <div class="col-span-6">
                    <jet-label for="lastname">{{ $t('agencies.lastname') }} <RequiredMark /></jet-label>
                    <jet-input id="lastname" type="text" class="mt-1 block w-full" v-model="form.lastname" />
                    <jet-input-error :message="form.errors.lastname" class="mt-2" />
                </div>

                <div class="col-span-6">
                    <jet-label for="email">{{ $t('agencies.email') }} <RequiredMark /></jet-label>
                    <jet-input id="email" type="email" class="mt-1 block w-full" v-model="form.email" />
                    <jet-input-error :message="form.errors.email" class="mt-2" />
                </div>

                <div class="col-span-6">
                    <jet-label for="mobile">{{ $t('agencies.mobile') }} <RequiredMark /></jet-label>
                    <input id="mobile" type="text" 
                           class="mt-1 block w-full border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 shadow-sm" 
                           v-model="form.mobile" 
                           :maxlength="$page.props.branding.mobile_number_max_digit || 12"
                           @input="form.mobile = form.mobile.replace(/[^0-9]/g, '').slice(0, $page.props.branding.mobile_number_max_digit || 12)" />
                    <jet-input-error :message="form.errors.mobile" class="mt-2" />
                </div>

                <!-- Role -->
                <div class="col-span-6" v-if="assignableRoles.length > 0">
                    <jet-label for="roles">{{ $t('agencies.role') }} <RequiredMark /></jet-label>
                    <jet-input-error :message="form.errors.role" class="mt-2" />

                    <div class="relative z-0 mt-1  border border-gray-200 rounded-lg cursor-pointer">
                        <button type="button"
                            class="relative px-4 py-3 inline-flex w-full rounded-lg focus:z-10 focus:outline-none focus:border-blue-300 focus:ring focus:ring-blue-200"
                            :class="{'border-t border-gray-200 rounded-t-none': i > 0, 'rounded-b-none': i != Object.keys(assignableRoles).length - 1,}"
                            @click="form.role = role.key" v-for="(role, i) in assignableRoles" :key="role.key">
                            <div :class="{'opacity-50': form.role && form.role != role.key, }">

                                <div class="flex items-center">
                                    <div class="text-sm text-gray-600" :class="{'font-semibold': form.role == role.key,}">
                                        {{ role.name }}
                                    </div>

                                    <svg v-if="form.role == role.key" class="ml-2 h-5 w-5 text-green-400" fill="none" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" stroke="currentColor" viewBox="0 0 24 24">
                                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                    </svg>
                                </div>

                                <div class="mt-2 text-xs text-gray-600 text-left">
                                    {{ role.description }}
                                </div>
                            </div>
                        </button>
                    </div>
                </div>
            </form-card>

        </template>

        <template #actions>
            <div class="flex justify-end my-4 mx-6">

                <jet-action-message :on="form.recentlySuccessful" class="mr-3">
                    {{ $t('actions.added') }}
                </jet-action-message>

                <button @click="cancel" type="button" class="inline-block uppercase font-semibold text-xs tracking-widest">
                    {{ $t("actions.cancel") }}
                </button>

                <primary-button :class="{ 'opacity-25': form.processing }" :disabled="form.processing" class="ml-3">
                    {{ $t('actions.add') }}
                </primary-button>

            </div>
        </template>
    </jet-form-section>

</template>

<script>
import JetFormSection from "@/Jetstream/FormSection.vue";
import PrimaryButton from "@/Jetstream/PrimaryButton";
import JetActionMessage from "@/Jetstream/ActionMessage.vue";
import JetInput from "@/Jetstream/Input.vue";
import JetInputError from "@/Jetstream/InputError.vue";
import JetLabel from "@/Jetstream/Label.vue";
import PageTitle from "@/Shared/Components/PageTitle";
import FormCard from "@/Shared/Components/FormCard";
import RequiredMark from "@/Shared/Components/RequiredMark";

export default {
    props: ['agency', 'availableRoles', "links", "permissions"],
    emits: ['closeModal'],
    inheritAttrs: false,
    components: {
        JetFormSection,
        PrimaryButton,
        JetActionMessage,
        JetInput,
        JetInputError,
        JetLabel,
        PageTitle,
        FormCard,
        RequiredMark
    },
    data() {
        return {
            form: this.$inertia.form({
                firstname: "",
                lastname: "",
                email: "",
                mobile: "",
                role: "manager"
            }),
        };
    },
    computed: {
        assignableRoles() {
            let roles = this.availableRoles.filter(r => r.key !== 'advisor');
            
            // Only allow owner role if the user has permission to add owners
            if (this.permissions.canAddOwner) {
                return roles;
            }
            
            return roles.filter(r => r.key !== 'owner');
        }
    },
    methods: {
        addTeamMember() {
            this.form.post(this.route(this.links.invite, this.agency), {
                errorBag: "addTeamMember",
                preserveScroll: true,
                onSuccess: () => this.$notify({
                    group: "success",
                    title: "actions.success",
                    text: "agencies.inviteSent"
                })
            });
        },
        cancel() {
            this.$emit("closeModal");
        },
    }
};
</script>

<style scoped>

</style>
